import React, { useState } from 'react';
import { theme } from '../theme';
import { getApiUrl } from '../config';

interface ForgotPasswordModalProps {
  isOpen: boolean;
  initialEmail?: string;
  onClose: () => void;
}

const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({ isOpen, initialEmail = '', onClose }) => {
  const [email, setEmail] = useState(initialEmail);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  React.useEffect(() => {
    if (isOpen) {
      setEmail(initialEmail);
      setSent(false);
      setError('');
    }
  }, [isOpen, initialEmail]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Ingresá un email');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(getApiUrl('/api/auth/forgot-password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'No se pudo enviar el email');
      }
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 420,
          background: theme.colors.surface,
          border: `1px solid ${theme.colors.border}`,
          borderRadius: 16,
          padding: 24,
          boxShadow: '0 12px 32px rgba(0,0,0,0.4)',
        }}
      >
        <h2 style={{ margin: '0 0 8px 0', color: theme.colors.text, fontSize: 20, fontWeight: 700 }}>
          {sent ? 'Email enviado' : 'Restablecer contraseña'}
        </h2>
        <p style={{ margin: '0 0 20px 0', color: theme.colors.textSecondary, fontSize: 14, lineHeight: 1.5 }}>
          {sent
            ? `Si ${email} está registrado, te enviamos un link para crear una nueva contraseña. Revisá tu casilla (y la carpeta de spam).`
            : 'Ingresá tu email y te enviamos un link para crear una nueva contraseña.'}
        </p>

        {!sent && (
          <form onSubmit={handleSubmit}>
            <input
              type="email"
              placeholder="tu@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
              required
              style={{
                width: '100%',
                padding: '14px 16px',
                backgroundColor: theme.colors.background,
                border: `1px solid ${theme.colors.border}`,
                borderRadius: theme.borderRadius.input,
                color: theme.colors.text,
                fontSize: theme.fonts.sizeBody,
                fontFamily: theme.fonts.body,
                outline: 'none',
                boxSizing: 'border-box',
                marginBottom: 12,
              }}
            />

            {error && (
              <div style={{ color: theme.colors.danger, fontSize: 13, marginBottom: 12, textAlign: 'center' }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: 'transparent',
                  color: theme.colors.textSecondary,
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: theme.borderRadius.button,
                  cursor: 'pointer',
                  fontSize: theme.fonts.sizeBody,
                  fontWeight: 600,
                }}
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: theme.colors.primary,
                  color: '#000',
                  border: 'none',
                  borderRadius: theme.borderRadius.button,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: theme.fonts.sizeBody,
                  fontWeight: 700,
                  opacity: loading ? 0.7 : 1,
                }}
              >
                {loading ? 'Enviando…' : 'Enviar link'}
              </button>
            </div>
          </form>
        )}

        {sent && (
          <button
            type="button"
            onClick={onClose}
            style={{
              width: '100%',
              padding: '12px',
              background: theme.colors.primary,
              color: '#000',
              border: 'none',
              borderRadius: theme.borderRadius.button,
              cursor: 'pointer',
              fontSize: theme.fonts.sizeBody,
              fontWeight: 700,
            }}
          >
            Entendido
          </button>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordModal;
